/**
 * Analyst-facing wording for text the agents and the verifier wrote for the system, not for a reader (AIC-557): field ids
 * become their labels, status / kind / reason codes become words, tool names become what the tool does, and raw JSON
 * becomes "Key: value" lines. The stored text is never changed (dedupe keys); a caller that changes the text shows the
 * original under TechnicalDetails.
 */
import type { ReactNode } from "react";
import { periodCodesInText } from "@swtracker/core/period-label";
import { UNIT_LABEL } from "@swtracker/core/display-labels";
import { calculationLabel, labelFieldIds, outsideLinks } from "../screens/resultFields.ts";
import { END_REASON_LABELS } from "./interviewOutcome.tsx";
import { REVIEW_KINDS } from "./reviewKinds.tsx";
import { isSubStatus, subStatus } from "./subStatus.tsx";

const words = (s: string) => s.replace(/_/g, " ");
const sentenceCase = (s: string) => s.replace(/^./, (c) => c.toUpperCase());
const TOOLS = /\b(?:calendarize_figure|check_period_share)\b/g;
/** The review-item reasons the voice agent attaches in brackets or after "reason:" (voice-gateway interview-plan). */
const REASON_WORDS: Readonly<Record<string, string>> = {
  confirm_over_correction: "seemed to agree with IDC's figure",
};

/** Tier names as the Results page shows them: "gold" → "Gold". */
export const tierLabel = (tier: string | null | undefined): string => (tier ? sentenceCase(tier) : "—");
/**
 * What a tier says, in one plain line (the verifier's rubric, agents/prompts/post-call-verifier.md): how much the interview
 * captured, never whether a figure is right. An unknown tier has no line.
 */
export const TIER_MEANING: Readonly<Record<string, string>> = {
  gold: "a total revenue or growth figure was confirmed or corrected, with a breakdown, commentary and no unresolved disagreements.",
  silver: "a total revenue or growth figure was confirmed or corrected with commentary but no breakdown, or Gold apart from one or two unclear answers or one disagreement.",
  bronze: "commentary without a revenue figure or breakdown, or an interview with several disagreements or major required topics missing.",
  failed: "the interview didn't complete or engage, consent wasn't recorded, or nothing usable was captured.",
};
/** "Gold tier: a total revenue or growth figure was …"; null for no tier or one the rubric doesn't name. */
export const tierMeaning = (tier: string | null | undefined): string | null => (tier && TIER_MEANING[tier] ? `${tierLabel(tier)} tier: ${TIER_MEANING[tier]}` : null);
/** How an interview was run: the session's `mode`. */
export const interviewModeLabel = (mode: string | null | undefined): string => (mode === "voice" ? "Voice" : mode === "form" ? "Text form" : mode ? sentenceCase(words(mode)) : "—");

function fromJson(text: string): string | null {
  const t = text.trim();
  if (!/^[[{]/.test(t)) return null;
  try {
    const v: unknown = JSON.parse(t);
    const entries = Array.isArray(v) ? v.map((x, i) => [String(i + 1), x] as const) : v && typeof v === "object" ? Object.entries(v) : [];
    const shown = entries.filter(([, x]) => x === null || ["string", "number", "boolean"].includes(typeof x));
    return shown.map(([k, x]) => `${sentenceCase(words(k))}: ${x === null ? "none" : typeof x === "boolean" ? (x ? "yes" : "no") : String(x)}`).join(" · ") || null;
  } catch { return null; }
}

/**
 * Agent-written evidence or a review reason with its KNOWN codes worded: catalogue field ids, units, tool names, end
 * reasons, review kinds and the voice agent's reason codes. Nothing else is touched or removed: an email, a URL, a
 * quoted phrase or a code this does not know stays exactly as written (accuracy over tidiness).
 */
export function displayText(text: string): string {
  const j = fromJson(text);
  let t = j ?? text;
  t = periodCodesInText(outsideLinks(t, labelFieldIds));
  t = t.replace(TOOLS, (tool) => calculationLabel(tool));
  t = t.replace(/\bend_reason\s*[:=]\s*([a-z0-9_]+)/gi, (all, r: string) => (END_REASON_LABELS[r] ? `the call ended: ${END_REASON_LABELS[r]}` : all));
  // A known code in brackets: "(vendor_request)", "(confirm_over_correction)", "(custom_question)".
  t = t.replace(/\(\s*([a-z0-9]+(?:_[a-z0-9]+)+)\s*\)/g, (all, code: string) => {
    const w = REASON_WORDS[code] ?? END_REASON_LABELS[code] ?? (REVIEW_KINDS[code] ? REVIEW_KINDS[code]!.label.toLowerCase() : null);
    return w ? `(${w})` : all;
  });
  t = t.replace(/\b(unit)\s*[:=]\s*([a-z_]+)\b/gi, (all, k: string, u: string) => (UNIT_LABEL[u] ? `${k}: ${UNIT_LABEL[u]}` : all));
  t = t.replace(/\busd_m\b/g, "USD millions");
  // Only a lowercase machine prefix with a code this knows; a vendor's own "Status: we are private now" is left exactly as written.
  t = t.replace(/(?<![\w])(kind|status)\s*[:=]\s*([a-z0-9]+(?:_[a-z0-9]+)*)\b/g, (all, k: string, code: string) => {
    if (k === "kind") return REASON_WORDS[code] ?? (REVIEW_KINDS[code] ? REVIEW_KINDS[code]!.label : all);
    return isSubStatus(code) ? `Status: ${subStatus(code).label}` : all;
  });
  return t;
}

/** The same text in a disclosure, for the person who needs the exact stored words; nothing when the display text is the text. */
export function TechnicalDetails({ raw, shown }: { raw: string; shown: string }): ReactNode {
  // Whitespace alone is not a change worth a disclosure.
  if (raw.replace(/\s+/g, " ").trim() === shown.replace(/\s+/g, " ").trim()) return null;
  return <details className="ipd-tech"><summary>Technical details</summary><p>{raw}</p></details>;
}
